import { KeyboardEvent, MouseEvent, useMemo } from 'react';
import { RenderIf } from 'react-rainbow-components';
import { EntityGet } from 'data/firestore/types';
import { Notification } from 'data/firestore/agent/user/notification/types';
import { formatRelativeDate } from 'data/services/date/formatter';
import {
    MarkReadButton,
    NotificationButton,
    NotificationContent,
    NotificationDate,
    NotificationRow,
    NotificationSubtitle,
    NotificationTitle,
    RowActions,
    UnreadDot,
} from './styled';

interface NotificationItemProps {
    notification: EntityGet<Notification>;
    isProcessing: boolean;
    onOpen: (notification: EntityGet<Notification>) => void;
    onMarkRead: (event: MouseEvent<HTMLButtonElement>, notificationId: string) => void;
}

const parseDateToTimestamp = (value: unknown): number => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? 0 : value.getTime();
    }
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? 0 : parsed;
    }
    if (typeof value === 'object' && value !== null) {
        const asDateValue = value as { toMillis?: () => number; seconds?: number };
        if (typeof asDateValue.toMillis === 'function') {
            return asDateValue.toMillis();
        }
        if (typeof asDateValue.seconds === 'number') {
            return asDateValue.seconds * 1000;
        }
    }
    return 0;
};

const NotificationItem = ({
    notification,
    isProcessing,
    onOpen,
    onMarkRead,
}: NotificationItemProps) => {
    const isUnread = notification.status === 'unread';
    const notificationTitle = notification.title || 'Notification';
    const notificationBody = notification.body || 'Open opportunity notes';

    const relativeDate = useMemo(
        () => {
            const createdAt = parseDateToTimestamp(notification.createdAt)
                || parseDateToTimestamp(notification.updatedAt);
            return createdAt ? formatRelativeDate(createdAt) : 'Recently';
        },
        [notification.createdAt, notification.updatedAt],
    );

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(notification);
        }
    };

    return (
        <NotificationRow>
            <NotificationButton
                $isUnread={isUnread}
                role="button"
                tabIndex={0}
                onClick={() => onOpen(notification)}
                onKeyDown={handleKeyDown}
            >
                <UnreadDot $isVisible={isUnread} />
                <NotificationContent>
                    <NotificationTitle $isUnread={isUnread}>
                        {notificationTitle}
                    </NotificationTitle>
                    <NotificationSubtitle>
                        {notificationBody}
                    </NotificationSubtitle>
                    <NotificationDate>{relativeDate}</NotificationDate>
                    <RenderIf isTrue={isUnread}>
                        <RowActions>
                            <MarkReadButton
                                type="button"
                                disabled={isProcessing}
                                onClick={(event) => onMarkRead(event, notification.id)}
                            >
                                Mark as read
                            </MarkReadButton>
                        </RowActions>
                    </RenderIf>
                </NotificationContent>
            </NotificationButton>
        </NotificationRow>
    );
};

export default NotificationItem;
